import { useFormik } from 'formik';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { patchUser, postUser } from '../services/users';
import logger from '../utils/logger';
import { sexOptions } from '../configs/users';
import { updateGroupRoles } from '../services/groups';

export const useFormikUser = (formikConfig, user = {}) => {
    const { t } = useTranslation();
    const indexSex = sexOptions.findIndex((value) => value === user?.sex);
    const sex =
        t('sexOptions', { returnObjects: true }).find(
            (option) => option.value === indexSex
        ) || '';

    return useFormik({
        initialValues: {
            type: 0,
            name: user?.name || '',
            sex,
            gender: user?.gender || '',
            birthdate: user?.birth_date ? new Date(user.birth_date) : null,
            address: user?.address?.street || '',
            zip: user?.address?.zip || '',
            city: user?.address?.city || '',
            phone: user?.phone || '',
            email: user?.email || '',
            healthSystemType: user?.health_id_type ?? 0,
            healthSystemNumber: user?.health_id || '',
            maxHeartRate: user?.user_data?.maxHeartRate || '',
            trainingHeartRate: user?.user_data?.trainingHeartRate || '',
            minTrainingHeartRate: user?.user_data?.minTrainingHeartRate || '',
            restingHeartRate: user?.user_data?.restingHeartRate || '',
            anaerobicThreshold: user?.user_data?.anaerobicThreshold || '',
            ventilatoryThreshold: user?.user_data?.ventilatoryThreshold || '',
            professionalCertificate: user?.user_data?.professionalCertificate || '',
            speciality: user?.user_data?.speciality || '',
            height: user?.user_data?.height || '',
            weight: user?.user_data?.weight || '',
            vo2Max: user?.user_data?.vo2Max || '',
            trainingBackgorund: user?.user_data?.trainingBackgorund || '',
            typicalDay: user?.user_data?.typicalDay || '',
            sleepGoalMinutes: user?.user_data?.sleepGoalMinutes || '',
            picture: null,
            submit: null,
            groupsToAssign: []
        },
        enableReinitialize: true,
        validationSchema: Yup.object().shape({
            name: Yup.string()
                .max(255, 'O nome pode ter no máximo 255 caracteres')
                .required('O nome é obrigatório'),
            address: Yup.string().max(
                255,
                'O endereço pode ter no máximo 255 caracteres'
            ),
            city: Yup.string().max(255, 'O endereço pode ter no máximo 255 caracteres'),
            postalCode: Yup.string().max(
                20,
                'O código postal pode ter no máximo 20 caracteres'
            ),
            phone: Yup.number().typeError('O telefone deve ser um número'),
            email: Yup.string()
                .email('Deve ser um email válido')
                .max(255, 'O email pode ter no máximo 255 caracteres')
                .required('O email é obrigatório'),
            maxHeartRate: Yup.number()
                .positive('Deve ser um valor positivo')
                .integer('Deve ser um número inteiro'),
            trainingHeartRate: Yup.number()
                .positive('Deve ser um valor positivo')
                .integer('Deve ser um número inteiro'),
            minTrainingHeartRate: Yup.number()
                .positive('Deve ser um valor positivo')
                .integer('Deve ser um número inteiro'),
            restingHeartRate: Yup.number()
                .positive('Deve ser um valor positivo')
                .integer('Deve ser um número inteiro'),
            anaerobicThreshold: Yup.number().positive('Deve ser um valor positivo'),
            ventilatoryThreshold: Yup.number().positive('Deve ser um valor positivo')
            ,
            height: Yup.number().positive('Deve ser um valor positivo'),
            weight: Yup.number().positive('Deve ser um valor positivo'),
            vo2Max: Yup.number().positive('Deve ser um valor positivo'),
            sleepGoalMinutes: Yup.number()
                .positive('Deve ser um valor positivo')
                .integer('Deve ser um número inteiro')
        }),
        ...formikConfig
    });
};

export const useFormikAddUser = (preselectedType, options = {}) => {
    const navigate = useNavigate();

    const { redirectTo = '/users', redirectState = null } = options;

    return useFormikUser({
        initialValues: {
            type: preselectedType ?? 0,
            name: '',
            sex: '',
            gender: '',
            birthdate: null,
            address: '',
            zip: '',
            city: '',
            phone: '',
            email: '',
            healthSystemType: 0,
            healthSystemNumber: '',
            maxHeartRate: '',
            trainingHeartRate: '',
            minTrainingHeartRate: '',
            restingHeartRate: '',
            anaerobicThreshold: '',
            ventilatoryThreshold: '',
            professionalCertificate: '',
            speciality: '',
            height: '',
            weight: '',
            vo2Max: '',
            trainingBackgorund: '',
            typicalDay: '',
            sleepGoalMinutes: '',
            picture: null,
            submit: null,
            groupsToAssign: []
        },
        onSubmit: async (data, { setErrors, setSubmitting }) => {
            setSubmitting(true);

            try {
                const response = await postUser(data);
                const newRoleUuid = response?.data?.role_uuid;

                if (data.groupsToAssign?.length > 0 && newRoleUuid) {
                    for (const groupUuid of data.groupsToAssign) {
                        await updateGroupRoles({
                            uuid: groupUuid,
                            to_add: [newRoleUuid],
                            to_remove: []
                        });
                    }
                }

                navigate(redirectTo, {
                    replace: true,
                    state: redirectState
                });
            } catch (error) {
                logger.info('Erro ao criar utilizador:', error);
                setErrors({ submit: 'Erro ao criar novo utilizador' });
            } finally {
                setSubmitting(false);
            }
        }
    });
};

export const useFormikEditUser = (user) => {
    const navigate = useNavigate();

    return useFormikUser(
        {
            onSubmit: async (data, { setErrors, setSubmitting }) => {
                setSubmitting(true);

                try {
                    await patchUser(data, user.role_uuid);
                    navigate('/users', { replace: true });
                } catch (error) {
                    setErrors({ submit: 'Erro ao editar utilizador' });
                    console.error('Erro ao editar utilizador:', error);
                } finally {
                    setSubmitting(false);
                }
            }
        },
        user
    );
};

export const useFormikEditClinicUser = (userData, user, clinic) => {
    const navigate = useNavigate();

    return useFormikUser(
        {
            onSubmit: async (data, { setErrors, setSubmitting }) => {
                setSubmitting(true);

                try {
                    const {
                        maxHeartRate,
                        trainingHeartRate,
                        minTrainingHeartRate,
                        restingHeartRate,
                        anaerobicThreshold,
                        ventilatoryThreshold,
                        professionalCertificate,
                        speciality,
                        height,
                        weight,
                        vo2Max,
                        trainingBackgorund,
                        typicalDay,
                        sleepGoalMinutes,
                        ...safeData
                    } = data;

                    await patchUser(safeData, user.role_uuid);

                    navigate('/mgmt/clinic/users', {
                        replace: true,
                        state: clinic ? { clinic } : undefined
                    });
                } catch (error) {
                    setErrors({ submit: 'Erro ao editar utilizador' });
                    console.error(error);
                } finally {
                    setSubmitting(false);
                }
            }
        },
        userData.data
    );
};
